import React, { useEffect, useRef } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useToast } from "../ui/Toast";
import { pickLanguage, useLanguage } from "../ui/LanguageProvider";
import { ROLE_RANK, useAuth } from "./AuthContext";
import type { Role } from "./AuthContext";
import { useMenuRoles } from "./MenuRoleContext";

/**
 * 라우트 인가 래퍼 — AuthGuard(인증) 안쪽에서 role을 검사한다.
 * role이 최소 등급 미만이면 /home으로 리다이렉트 + 토스트.
 * children 없이 쓰면 <Outlet/>을 렌더링한다(라우트 그룹용).
 *
 * 1-b: menuKey를 주면 DB 매핑(menu_role_map)의 등급을 우선 적용하고 minRole은 폴백이 된다.
 * 설정 관리자 라우트는 menuKey "settings:*" 를 쓴다(settingsMenus.ts) — 등급·노출 모두 DB 로 조정,
 * super_admin 은 등급 판정 항상 통과, 메뉴 권한 관리는 숨김 불가(자물쇠 방지).
 */
export default function RoleGuard({
  minRole,
  menuKey,
  children,
}: {
  minRole: Role;
  menuKey?: string;
  children?: React.ReactNode;
}) {
  const { status, role } = useAuth();
  const { getEffectiveMinRole, getEffectiveEnabled, loaded } = useMenuRoles();
  const toast = useToast();
  const { language } = useLanguage();
  const toastedRef = useRef(false);

  // menuKey가 있으면 상속 적용 등급(상위 메뉴 floor 포함)으로 판정
  const effectiveMinRole = menuKey ? getEffectiveMinRole(menuKey, minRole) : minRole;
  // 미사용 메뉴는 role 과 무관하게 접근 차단 — 메뉴만 숨기고 URL 이 열려 있으면 반쪽이다.
  // menuKey 없는 라우트(/admin/*)는 정적 가드라 이 판정을 타지 않는다(자물쇠 방지).
  const menuDisabled = menuKey ? !getEffectiveEnabled(menuKey) : false;
  const allowed =
    status === "authenticated" &&
    !menuDisabled &&
    (role === "super_admin" || ROLE_RANK[role ?? "viewer"] >= ROLE_RANK[effectiveMinRole]);

  // menuKey 판정은 DB 매핑이 있어야 정확하다 — 로드 전 코드 기본값으로 판정하면 권한을 낮춘 메뉴에
  // 딥링크로 들어온 사람이 잘못 튕긴다. 로드 실패도 loaded=true 로 끝나므로 영원히 멈추지 않는다.
  const waiting = !!menuKey && !loaded;

  useEffect(() => {
    if (waiting) return;
    if (status === "authenticated" && !allowed && !toastedRef.current) {
      toastedRef.current = true;
      toast.show({
        type: "error",
        message: menuDisabled
          ? pickLanguage(language, "현재 사용하지 않는 메뉴입니다.", "This menu is currently disabled.")
          : pickLanguage(language, "접근 권한이 없습니다.", "Access denied."),
      });
    }
  }, [waiting, status, allowed, menuDisabled, toast]);

  if (status === "loading" || waiting) return null;
  // 미인증은 AuthGuard가 /login으로 처리 — 여기서는 렌더링만 보류
  if (status === "unauthenticated") return null;
  if (!allowed) return <Navigate to="/home" replace />;
  return children !== undefined ? <>{children}</> : <Outlet />;
}
